import { useState } from "react";
import { api, idr, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const FIELDS = [["customer_company", "Kepada (Perusahaan)"], ["customer_phone", "Phone"], ["attention", "Att"], ["subject", "Hal"],
  ["customer_name", "Nama Kontak"], ["customer_email", "Email"], ["po_number", "No. PO Customer"]];
const ADDR = [["street", "Alamat"], ["city", "Kota / Kab."], ["state", "Provinsi"], ["zip", "Kode Pos"]];
const NUM = ["quantity", "purchase_price", "margin_percent"];
const blank = { product_name: "", series: "", category: "", quantity: 1, unit: "Unit", purchase_price: 0, margin_percent: 15 };

function ItemRow({ it, i, set, del }) {
  const unit = (Number(it.purchase_price) || 0) * (1 + (Number(it.margin_percent) || 0) / 100);
  return (
    <div className="grid grid-cols-12 gap-2 items-end border-b border-border pb-2" data-testid={`qedit-item-${i}`}>
      <div className="col-span-12 md:col-span-4"><Label className="text-[10px]">Description</Label><Input value={it.product_name} onChange={(e) => set(i, "product_name", e.target.value)} data-testid={`qedit-item-name-${i}`} /></div>
      <div className="col-span-6 md:col-span-2"><Label className="text-[10px]">Detail / Seri</Label><Input value={it.series || ""} onChange={(e) => set(i, "series", e.target.value)} /></div>
      <div className="col-span-2 md:col-span-1"><Label className="text-[10px]">Unit</Label><Input type="number" min="1" value={it.quantity} onChange={(e) => set(i, "quantity", e.target.value)} data-testid={`qedit-item-qty-${i}`} /></div>
      <div className="col-span-4 md:col-span-2"><Label className="text-[10px]">Harga Beli (internal)</Label><Input type="number" value={it.purchase_price} onChange={(e) => set(i, "purchase_price", e.target.value)} data-testid={`qedit-item-cost-${i}`} /></div>
      <div className="col-span-3 md:col-span-1"><Label className="text-[10px]">Margin %</Label><Input type="number" value={it.margin_percent} onChange={(e) => set(i, "margin_percent", e.target.value)} data-testid={`qedit-item-margin-${i}`} /></div>
      <div className="col-span-7 md:col-span-1 text-xs font-mono pb-2" title="Unit Price">{idr(unit)}</div>
      <div className="col-span-2 md:col-span-1 flex justify-end"><Button variant="ghost" size="sm" onClick={() => del(i)} data-testid={`qedit-item-del-${i}`}><Trash2 className="w-4 h-4 text-red-500" /></Button></div>
    </div>
  );
}

export function QuotationEditDialog({ q, onClose, onSaved }) {
  const [f, setF] = useState({ ...q, items: q.items.map((i) => ({ ...i })) });
  const [busy, setBusy] = useState(false);
  const setItem = (i, k, v) => setF({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) });
  const save = async () => {
    setBusy(true);
    try {
      const items = f.items.filter((i) => i.product_name.trim()).map((i) => ({ ...i, ...Object.fromEntries(NUM.map((k) => [k, Number(i[k]) || 0])) }));
      const body = { ...Object.fromEntries(FIELDS.map(([k]) => [k, f[k] || ""])), customer_address: f.customer_address || null,
        items, tax_percent: Number(f.tax_percent) || 0, notes: f.notes || "" };
      const { data } = await api.put(`/quotations/${q.id}`, body);
      toast.success("Penawaran diperbarui"); onSaved(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menyimpan"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="quotation-edit-dialog">
        <DialogHeader><DialogTitle>Edit Penawaran {q.quote_number}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {FIELDS.map(([k, l]) => (
            <div key={k}><Label className="text-xs">{l}</Label><Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`qedit-${k}`} /></div>
          ))}
          {ADDR.map(([k, l]) => (
            <div key={k} className={k === "street" ? "md:col-span-2" : ""}><Label className="text-xs">{l}</Label>
              <Input value={(f.customer_address || {})[k] || ""} onChange={(e) => setF({ ...f, customer_address: { country: "Indonesia", ...(f.customer_address || {}), [k]: e.target.value } })} data-testid={`qedit-addr-${k}`} /></div>
          ))}
          <div><Label className="text-xs">PPN %</Label><Input type="number" value={f.tax_percent} onChange={(e) => setF({ ...f, tax_percent: e.target.value })} data-testid="qedit-tax" /></div>
        </div>
        <div className="space-y-2 mt-2">
          <div className="text-xs font-semibold text-muted-foreground">Item (Description · Unit · Unit Price IDR)</div>
          {f.items.map((it, i) => <ItemRow key={i} it={it} i={i} set={setItem} del={(j) => setF({ ...f, items: f.items.filter((_, x) => x !== j) })} />)}
          <Button variant="outline" size="sm" onClick={() => setF({ ...f, items: [...f.items, { ...blank }] })} data-testid="qedit-add-item"><Plus className="w-4 h-4 mr-1" /> Tambah item</Button>
        </div>
        <div><Label className="text-xs">Catatan / NB (satu poin per baris)</Label><Textarea rows={3} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="qedit-notes" /></div>
        <Button onClick={save} disabled={busy} className="w-full" data-testid="qedit-save-btn">{busy ? "Menyimpan…" : "Simpan Perubahan"}</Button>
      </DialogContent>
    </Dialog>
  );
}
